import { useMemo } from "react";

import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse, useSwrGet } from "../api-client/useSwrGet";
import { AnyProductType } from "../constants/productTypes";
import { LanguageMap } from "../i18n/useLanguage";
import {
  DealerProductDetailsRoute,
  DealerVehicleResultsRoute,
} from "../navigation/DealerRoutes";
import { useDealerRoutesParams } from "../navigation/useDealerRoutesParams";
import { useSomeRoutes } from "../navigation/useSomeRoutes";
import { useDealerAccountInfo } from "../stores/hooks/useDealerAccountInfo";
import { useUrl } from "../stores/hooks/useUrl";
import { productTypeToPartType } from "../util/productTypeToPartType";
import { VehicleSubmodel } from "./useGetVehicleSubmodels";
import { useValidateVehicleKeys } from "./useValidateVehicleKeys";

export const useGetVehicleInfoByVehicleKeys = (): ModifiedSWRResponse<
  FetcherResult<VehicleInfoByVehicleKeysPayload>,
  VehicleInfoByVehicleKeysPayload
> => {
  const route = useDealerRoutesParams();
  const isRouteSupported = useSomeRoutes(
    DealerVehicleResultsRoute,
    DealerProductDetailsRoute
  );

  const { data: keys } = useValidateVehicleKeys();
  const { dealerNationalAccountId } = useDealerAccountInfo();
  const { searchParams } = useUrl();
  const sizeParam = searchParams.get("size");
  const partType =
    productTypeToPartType(route?.productType as AnyProductType) || "";

  const { data: submodels } = useSwrGet<VehicleSubmodel[]>(
    keys && sizeParam
      ? "/retailSiteWebService/vehicle/getVehicleSubmodels.php"
      : null,
    {
      dealerId: route?.dealerId ?? "",
      nationalAccountId: dealerNationalAccountId,
      vehicleCategoryId: String(keys?.vehicleCategoryId ?? ""),
      vehicleManufacturerId: String(keys?.vehicleManufacturerId ?? ""),
      vehicleModelId: String(keys?.vehicleModelId ?? ""),
      vehicleYearId: String(keys?.vehicleYearId ?? ""),
    }
  );

  const vehicleSubmodelId = useMemo((): string | undefined => {
    if (!sizeParam || !submodels) {
      return keys?.vehicleSubmodelId
        ? String(keys.vehicleSubmodelId)
        : undefined;
    }
    const match = submodels.find((sm) =>
      sm.vehicleSubmodelTiresizeArray.some(
        (ts) => ts.vehicleSubmodelTiresizeId === sizeParam
      )
    );
    return match?.vehicleSubmodelId ?? String(keys?.vehicleSubmodelId ?? "");
  }, [keys?.vehicleSubmodelId, sizeParam, submodels]);

  const shouldFetch =
    Boolean(keys) && isRouteSupported && Boolean(vehicleSubmodelId);
  const shouldWaitForSubmodels = Boolean(sizeParam) && !submodels && Boolean(keys);

  const result = useSwrGet<VehicleInfoByVehicleKeysPayload>(
    shouldFetch && !shouldWaitForSubmodels
      ? "/retailSiteWebService/vehicle/getVehicleInfoByVehicleKeys.php"
      : null,
    {
      dealerId: route?.dealerId ?? "",
      productType: partType ?? "",
      vehicleCategoryId: String(keys?.vehicleCategoryId ?? "null"),
      vehicleManufacturerId: String(keys?.vehicleManufacturerId ?? "null"),
      vehicleModelId: String(keys?.vehicleModelId ?? ""),
      vehicleSubmodelId: vehicleSubmodelId ?? "",
      vehicleYearId: String(keys?.vehicleYearId ?? ""),
    }
  );

  return result;
};

export type TireSize = {
  constructCode: string;
  fitmentCode: string;
  fitmentIsDRW: string;
  fitmentIsStaggered: string;
  formattedTireSize: string;
  hasWheels: string;
  isIdealTiresize: number;
  loadRating: string;
  outerDiameter: string;
  plyRating: string;
  ratio: string;
  rimDiameter: string;
  seasonalSizeCode: string;
  secondaryConstructCode: null | string;
  secondaryFormattedTireSize: string;
  secondaryHasWheels: string;
  secondaryLoadRating: string;
  secondaryOuterDiameter: null | string;
  secondaryPlyRating: string;
  secondaryRatio: null | string;
  secondaryRimDiameter: null | string;
  secondarySpeedIndex: string;
  secondaryTireSizeSpecId: string;
  secondaryType: null | string;
  secondaryWidth: null | string;
  sizeTypeCode: string;
  sizeTypeDescription: string;
  speedIndex: string;
  tireSizeSpecId: string;
  type: string;
  vehicleSubmodelTiresizeId: string;
  wheelQty: number;
  width: string;
};

type TireSizeList = {
  partCount: number;
  vehicleSubmodelTiresizeId: string;
};

export type SeasonNumber = 0 | 1 | 2;

type SeasonList = {
  partCount: number;
  season: SeasonNumber;
  tireSizeList: TireSizeList[];
};

type MetatagList = {
  [key: string]: {
    tag_id: string;
    tag_text: string;
    tag_text_fr: string;
  };
};

export type VehicleHasMappedParts = {
  ACC: number;
  PKG: number;
  TRE: number;
  WHL: number;
};

export type PartListSummary = {
  minLoadRating: number;
  minPlyRating: string;
  minSpeedRating: string;
  seasonList: SeasonList[];
};

export type VehicleInfoByVehicleKeysPayload = {
  metatagList: MetatagList;
  partListSummary: PartListSummary;
  vehicleChassisNumber: string;
  vehicleColourCodes: VehicleColourCode[];
  vehicleFullTireSizeInfo: TireSize[];
  vehicleFullWheelDiameterInfo: string[];
  vehicleHasMappedParts: VehicleHasMappedParts;
  vehicleId: string;
  vehicleManufacturerId: string;
  vehicleManufacturerName: string;
  vehicleModelId: string;
  vehicleModelImage: string;
  vehicleModelName: LanguageMap;
  vehicleOriginalTiresize: TireSize;
  vehicleRequiresRunFlatTires: boolean;
  vehicleSubmodelId: string;
  vehicleSubmodelImage?: string;
  vehicleSubmodelName: LanguageMap;
  vehicleVisualizerForegroundImageUrl: string;
  vehicleVisualizerShadowImageUrl: string;
  vehicleYear: string;
  vehicleYearId: string;
};

export type VehicleColourCode = {
  colourCode: string;
  colourCodeId: string;
  colourCodeName: LanguageMap;
  colourCodePatchImage: string;
  colourHexCode: string;
  visualizerShadowOpacity: string;
};

/**
 * @see docs/worklog/TW-893/TW-893-wrong-size-option/rfc.md
 */
